import { msg, t } from "@lingui/core/macro";
import { queryOptions, useQuery } from "@tanstack/react-query";
import z from "zod";
import { useOutletContext } from "react-router";
import { patientApi } from "./ApiClient";
import { generatePatches, generateQueryString } from "./Helpers";
import { siteSchema } from "@medoma/models/sites";
import {
  areasSchema,
  teamsSchema,
  patientTypesSchema,
  type IDeletedPatient,
  type IExistingPatient,
  type IPatientStatus,
  type IRelative,
  careEpisodeSchema,
  patientTypeSchema,
  personalIdentityNumberTypeSchema,
  relativeSchema,
  getPatientFullName,
  patientNameType,
  getPatientFamilyName,
  getPatientGivenName,
  economicUnitsSchema,
  givenAndFamilyNameSchema,
  careHubsSchema,
} from "@medoma/models/patients";
import type {
  IAdmittedPatient,
  IDeclinedPatient,
  IDischargedPatient,
  IGivenAndFamilyNameInput,
  IPersonalIdentityNumberType,
  IRegisteredPatient,
  listPatientSchema,
} from "@medoma/models/patients";
import {
  listAdmittedPatientSchema,
  listDischargedPatientSchema,
  listProspectPatientSchema,
  patientStatusSchema,
  patientStatusDictionary,
  wardsSchema,
  existingPatientSchema,
  listExistingPatientSchema,
  activeStatuses,
} from "@medoma/models/patients";
import {
  addressWithCoordinates,
  type IAddressWithCoordinates,
} from "@medoma/models/addresses";
import { i18n } from "@lingui/core";
import { fetchUncancelledHomeVisitActivityOccurrences } from "./Activities";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import {
  rawPhoneNumberSchema,
  type RawPhoneNumber,
} from "@medoma/models/phoneNumbers";
import { internalPostalCodeSchema } from "@medoma/models/postalCode";
import { fetchPatientsSessions } from "./Auth";

export type IListPatient = z.infer<typeof listPatientSchema>;

const existingPatientsSchema = z.array(listExistingPatientSchema);
export type IListExistingPatient = z.infer<typeof listExistingPatientSchema>;

const prospectPatientsSchema = z.array(listProspectPatientSchema);

const admittedPatientsSchema = z.array(listAdmittedPatientSchema);

const dischargedPatientsSchema = z.array(listDischargedPatientSchema);

const careEpisodeAddressSchema = addressWithCoordinates.extend({
  // Would be better to base on country code
  // Or make exhaustive somehow
  // This works OK for now
  postalCode: z.union([
    internalPostalCodeSchema.shape.CH,
    internalPostalCodeSchema.shape.LV,
    internalPostalCodeSchema.shape.SE,
  ]),
});

// The fields an organization can switch on for its patients, one value per field. Which of
// them a form asks for is the form's decision — the endpoints take any of them — so a
// payload declares the whole group and sends what it collected.
const careEpisodeInformationSchema = z.object({
  careSummary: z.string().nullish(),
  diagnosisCode: z.string().nullish(),
  externalCareEpisodeId: z.string().nullish(),
  flags: z.string().nullish(),
  healthcareJourneyNumber: z.string().length(8).nullish(),
  homeCare: z.boolean().nullish(),
  information: z.string().nullish(),
  language: z.string().nullish(),
  lifeCare: z.boolean().nullish(),
  paymentExempt: z.boolean().nullish(),
  pet: z.string().nullish(),
  placeOfAssessment: z.string().nullish(),
  plannedAdmissionDateTime: calendarDateSchema.nullish(),
  primaryResponsible: z.number().int().nullish(),
  postDischargeProcessed: z.boolean().nullish(),
  reasonForAdmission: z.string().nullish(),
  referringUnit: z.string().nullish(),
  safetyAlarmText: z.string().nullish(),
});

// What `POST /care-episodes` takes. The identity a registration settles, an address, and
// the care episode fields any write can carry.
const careEpisodeRegistrationSchema = z.object({
  careEpisodeId: z.uuid(),
  address: careEpisodeAddressSchema,
  name: givenAndFamilyNameSchema,
  patientType: patientTypeSchema,
  personalIdentityNumber: z.string(),
  personalIdentityNumberType: personalIdentityNumberTypeSchema,
  phoneNumber: rawPhoneNumberSchema.nullish(),
  // Site-specific, and referenced by id where the read schemas carry the resolved object. A
  // site can have no areas and no teams; one that does not use wards or economic units is
  // served a single default instead, which is what these carry there. The endpoint requires
  // an economic unit.
  area: z.number().positive().nullish(),
  team: z.number().positive().nullish(),
  ward: z.number().positive().nullish(),
  economicUnit: z.number().positive(),
  ...careEpisodeInformationSchema.shape,
});

// The endpoint decodes its body as "returning patient, or else new patient", and
// `relatives` is the key that decides: the returning half requires it, so a registration
// for a patient nobody has met must carry no `relatives` key at all — sending one, even an
// empty array, is answered with `PatientNotFound`.
// A new care episode for a patient the organization has cared for before.
const newCareEpisodeSchema = careEpisodeRegistrationSchema.extend({
  relatives: z.array(relativeSchema),
});
export type INewCareEpisode = z.infer<typeof newCareEpisodeSchema>;

// A patient the organization has never met before, registered as a prospect awaiting
// assessment: the same body, with the `relatives` key kept out by the type rather than by
// whoever writes the next call site.
const newProspectCareEpisodeSchema = careEpisodeRegistrationSchema.extend({
  relatives: z.never().optional(),
});
export type INewProspectCareEpisode = z.infer<
  typeof newProspectCareEpisodeSchema
>;

export const fetchPatients = async ({
  patientIds,
  statuses,
}: {
  patientIds?: string[];
  statuses?: IPatientStatus[];
}) => {
  const queryString =
    patientIds && patientIds.length > 100
      ? // MED-6269: We ran into ~8000 character limit in an invoicing scenario. Avoid this by asking for all patients instead.
        // This is OK, because the patientId filter is only used to reduce the load, not for functionality.
        generateQueryString({ status: statuses })
      : // This is normal usage
        generateQueryString({ id: patientIds, status: statuses });
  const parsedPatients = await patientApi
    .get(`/patients${queryString}`)
    .json(existingPatientsSchema);
  return parsedPatients;
};

const fetchProspectPatients = async ({
  patientIds,
}: {
  patientIds?: string[];
}) => {
  const queryString = generateQueryString({
    id: patientIds,
    status: patientStatusSchema.enum.prospect,
  });
  const parsedPatients = await patientApi
    .get(`/patients${queryString}`)
    .json(prospectPatientsSchema);
  return parsedPatients;
};

const fetchAdmittedPatients = async ({
  patientIds,
}: {
  patientIds?: string[];
}) => {
  const queryString = generateQueryString({
    id: patientIds,
    status: patientStatusSchema.enum.admitted,
  });
  const parsedPatients = await patientApi
    .get(`/patients${queryString}`)
    .json(admittedPatientsSchema);
  return parsedPatients.filter((patient) => !patient.pausedAt);
};

const fetchPausedPatients = async ({
  patientIds,
}: {
  patientIds?: string[];
}) => {
  const queryString = generateQueryString({
    id: patientIds,
    status: patientStatusSchema.enum.admitted,
  });
  const parsedPatients = await patientApi
    .get(`/patients${queryString}`)
    .json(admittedPatientsSchema);
  return parsedPatients.filter((patient) => patient.pausedAt);
};

const fetchDischargedPatients = async ({
  patientIds,
}: {
  patientIds?: string[];
}) => {
  const queryString = generateQueryString({
    id: patientIds,
    status: patientStatusSchema.enum.discharged,
  });
  const parsedPatients = await patientApi
    .get(`/patients${queryString}`)
    .json(dischargedPatientsSchema);
  return parsedPatients;
};

const fetchPatientTypes = async () => {
  const parsedPatientTypes = await patientApi
    .get(`/patients/types`)
    .json(patientTypesSchema);
  return parsedPatientTypes;
};

export const patientKeys = {
  all: ["patients"] as const,
  lists: () => [...patientKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...patientKeys.lists(), { filters }] as const,
  listWithVariant: (filters: Record<string, unknown>) =>
    [...patientKeys.lists(), "withHasHomeVisit", filters] as const,
  detail: (id: string) => [...patientKeys.all, id, "details"] as const,
};

const wardsKeys = {
  all: ["wards"] as const,
  detail: (id: number) => [...wardsKeys.all, id, "details"] as const,
};

const patientTypesKeys = {
  all: ["types"] as const,
};

const areasKeys = {
  all: ["areas"] as const,
  detail: (id: number) => [...areasKeys.all, id, "details"] as const,
};

const teamsKeys = {
  all: ["teams"] as const,
  detail: (id: number) => [...teamsKeys.all, id, "details"] as const,
};

const economicUnitsKeys = {
  all: ["economicUnit"] as const,
  detail: (id: number) => [...economicUnitsKeys.all, id, "details"] as const,
};

export const usePatients = ({
  statuses,
  sort = "name-asc",
}: {
  statuses: IPatientStatus[];
  sort?: "name-asc";
}) => {
  return useQuery({
    queryKey: patientKeys.list({ statuses, sort }),
    queryFn: async () =>
      (await fetchPatients({ statuses })).sort((a, b) => {
        switch (sort) {
          case "name-asc":
            return getPatientFullName(a.name).localeCompare(
              getPatientFullName(b.name),
            );
          default:
            return 0;
        }
      }),
  });
};

export const useProspectPatients = ({
  sort = "name-asc",
}: {
  sort?: "name-asc";
}) => {
  return useQuery({
    queryKey: patientKeys.list({
      statuses: [patientStatusSchema.enum.prospect],
      sort,
    }),
    queryFn: async () =>
      (await fetchProspectPatients({})).sort((a, b) => {
        switch (sort) {
          case "name-asc":
            return getPatientFullName(a.name).localeCompare(
              getPatientFullName(b.name),
            );
          default:
            return 0;
        }
      }),
  });
};

export const useAdmittedPatientsWithSignInStatus = ({
  sort = "name-asc",
}: {
  sort?: "name-asc";
}) => {
  return useQuery({
    queryKey: patientKeys.list({
      statuses: [patientStatusSchema.enum.admitted],
      sort,
    }),
    queryFn: async () => {
      const admittedPatients = (await fetchAdmittedPatients({})).sort(
        (a, b) => {
          switch (sort) {
            case "name-asc":
              return getPatientFullName(a.name).localeCompare(
                getPatientFullName(b.name),
              );
            default:
              return 0;
          }
        },
      );
      const patientSessions = await fetchPatientsSessions({
        statuses: [patientStatusSchema.enum.admitted],
      });
      const sessionMap = new Map(
        patientSessions.map((s) => [s.careEpisodeId, s.isSignedInToCare]),
      );
      return admittedPatients.map((patient) => ({
        ...patient,
        isSignedInToCare: sessionMap.get(patient.id) ?? false,
      }));
    },
  });
};

export const usePausedPatients = ({
  sort = "name-asc",
}: {
  sort?: "name-asc";
}) => {
  return useQuery({
    queryKey: patientKeys.list({
      statuses: [patientStatusSchema.enum.admitted],
      isPaused: true,
      sort,
    }),
    queryFn: async () =>
      (await fetchPausedPatients({})).sort((a, b) => {
        switch (sort) {
          case "name-asc":
            return getPatientFullName(a.name).localeCompare(
              getPatientFullName(b.name),
            );
          default:
            return 0;
        }
      }),
  });
};

export const dischargedPatientsQueryOptions = ({
  sort = "name-asc",
}: {
  sort?: "name-asc";
} = {}) => {
  return queryOptions({
    queryKey: patientKeys.list({
      statuses: [patientStatusSchema.enum.discharged],
      sort,
    }),
    queryFn: async () =>
      (await fetchDischargedPatients({})).sort((a, b) => {
        switch (sort) {
          case "name-asc":
            return getPatientFullName(a.name).localeCompare(
              getPatientFullName(b.name),
            );
          default:
            return 0;
        }
      }),
  });
};

export const usePatientTypes = () => {
  return useQuery({
    queryKey: patientTypesKeys.all,
    queryFn: () => fetchPatientTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes in milliseconds
  });
};

export const createDeletedPatient = (patientId: string) => ({
  id: patientId,
  status: patientStatusSchema.enum.deleted,
});

// Every patient id resolves to a patient: one that is missing from the list was
// deleted, and the stub stands in for it. A patient that is absent altogether is
// modelled by having no patientId to resolve, so callers that allow that decide
// what to do with it rather than asking here.
export const resolvePatient = ({
  patientId,
  patients,
}: {
  patientId: string;
  patients: ReadonlyArray<IListPatient>;
}): IListPatient =>
  patients.find((patient) => patient.id === patientId) ??
  createDeletedPatient(patientId);

export async function fetchPatient(id: string) {
  const parsedPatient = await patientApi
    .get(`/patients/${id}`)
    .json(existingPatientSchema);
  return parsedPatient;
}

export const usePatient = (patientId: string) => {
  return useQuery({
    queryKey: patientKeys.detail(patientId),
    queryFn: () => fetchPatient(patientId),
  });
};

export const patientsWithUnhandledMeasurementsSchema = z.array(
  z.object({
    id: z.uuid(),
    unhandledMeasurementsCount: z.number(),
  }),
);

async function fetchPatientsWithUnhandledMeasurements() {
  const parsedPatientIds = await patientApi
    .get(`/patients/unhandled-measurements`)
    .json(patientsWithUnhandledMeasurementsSchema);
  return parsedPatientIds;
}

export const patientsWithUnhandledMeasurementsQueryOptions = queryOptions({
  queryKey: patientKeys.list({ hasUnhandledMeasurements: true }),
  queryFn: fetchPatientsWithUnhandledMeasurements,
  // This request can get unnecessarily noisy (~1 request per navigation).
  // Prefer to re-use data and refresh using SSE.
  staleTime: Infinity,
  // Fall back to polling every 5 minutes to ensure relatively fresh data independent of SSE.
  refetchInterval: 1000 * 60 * 5,
});

export const fetchCareHub = async () => {
  const careHubs = await patientApi.get(`/carehubs`).json(careHubsSchema);

  if (!careHubs || !careHubs[0]) {
    throw new Error("No care hubs were found");
  }

  return careHubs[0];
};

export const useCareHub = () => {
  return useQuery({
    queryKey: careHubsKeys.all,
    queryFn: () => fetchCareHub(),
  });
};

const careHubsKeys = {
  all: ["careHubs"] as const,
};

export function useParentRoutesPatient() {
  return useOutletContext<IExistingPatient>();
}

export const dischargePatient = async ({
  patientId,
  dischargeDate,
  activitiesToClose,
}: {
  patientId: string;
  dischargeDate: Date;
  activitiesToClose?: string[];
}) => {
  await patientApi.post(`/patients/${patientId}/discharge`, {
    json: {
      dischargedAt: calendarDateSchema.encode(dischargeDate),
      activitiesToClose,
    },
  });
};

export const pausePatient = async ({
  patientId,
  pauseDate,
  activitiesToClose,
}: {
  patientId: string;
  pauseDate: Date;
  activitiesToClose?: string[];
}) => {
  await patientApi.post(`/patients/${patientId}/pause`, {
    json: {
      pausedAt: calendarDateSchema.encode(pauseDate),
      activitiesToClose,
    },
  });
};

export const unpausePatient = async (patientId: string) => {
  await patientApi.post(`/patients/${patientId}/unpause`);
};

export const declinePatient = async ({
  patientId,
  declinedAt,
  activitiesToClose,
}: {
  patientId: string;
  declinedAt: Date;
  activitiesToClose?: string[];
}) => {
  await patientApi.post(`/patients/${patientId}/decline`, {
    json: {
      declinedAt: calendarDateSchema.encode(declinedAt),
      activitiesToClose,
    },
  });
};

export const updateAddress = async (
  patientId: string,
  address: IAddressWithCoordinates,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ address }),
  });
};

export const updateDiagnosisCode = async (
  patientId: string,
  diagnosisCode: string,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ diagnosisCode }),
  });
};

export const updateExternalCareEpisodeId = async (
  patientId: string,
  externalCareEpisodeId: string,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ externalCareEpisodeId }),
  });
};

export const updatePlannedAdmissionDateTime = async (
  patientId: string,
  plannedAdmissionDateTime: Date | null,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({
      plannedAdmissionDateTime: calendarDateSchema
        .nullable()
        .encode(plannedAdmissionDateTime),
    }),
  });
};

export const updatePlannedDischargeDateTime = async (
  patientId: string,
  plannedDischargeDateTime: Date | null,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({
      plannedDischargeDateTime: calendarDateSchema
        .nullable()
        .encode(plannedDischargeDateTime),
    }),
  });
};

export const updatePostDischargeProcessed = async (
  patientId: string,
  postDischargeProcessed: boolean | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({
      postDischargeProcessed: Boolean(postDischargeProcessed),
    }), // For checkboxes, this is always a boolean
  });
};

export const updatePhoneNumber = async (
  patientId: string,
  phoneNumber: RawPhoneNumber,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ phoneNumber }),
  });
};

export const updateInformation = async (
  patientId: string,
  information: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ information }),
  });
};

export const updateName = async (
  patientId: string,
  givenAndFamilyName: IGivenAndFamilyNameInput,
) => {
  const name = {
    ...givenAndFamilyName,
    type: patientNameType.enum.GivenAndFamily,
  };
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ name }),
  });
};

export const updateWard = async (patientId: string, ward: number) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ ward }),
  });
};

export const updateArea = async ({
  patientId,
  area,
}: {
  patientId: string;
  area: number | null;
}) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ area }),
  });
};

export const updateTeam = async ({
  patientId,
  team,
}: {
  patientId: string;
  team: number | null;
}) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ team }),
  });
};

export const updateEconomicUnit = async ({
  patientId,
  economicUnit,
}: {
  patientId: string;
  economicUnit: number;
}) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ economicUnit }),
  });
};

export const addRelative = async (patientId: string, relative: IRelative) => {
  await patientApi.post(`/patients/${patientId}/relatives`, {
    json: { ...relative },
  });
};

export const updateRelative = async (
  patientId: string,
  relative: IRelative,
) => {
  await patientApi.patch(`/patients/${patientId}/relatives/${relative.id}`, {
    json: relative,
  });
};

export const removeRelative = async (patientId: string, relativeId: string) => {
  await patientApi.delete(`/patients/${patientId}/relatives/${relativeId}`);
};

export const updateHealthcareJourneyNumber = async (
  patientId: string,
  healthcareJourneyNumber: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ healthcareJourneyNumber }),
  });
};

export const updateSafetyAlarmText = async (
  patientId: string,
  safetyAlarmText: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ safetyAlarmText }),
  });
};

export const updatePets = async (
  patientId: string,
  pets: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ pet: pets }),
  });
};

export const updateFlags = async (
  patientId: string,
  flags: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ flags: flags }),
  });
};

export const updateCareSummary = async (
  patientId: string,
  careSummary: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ careSummary }),
  });
};

export const updateHomeCare = async (
  patientId: string,
  homeCare: boolean | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ homeCare: Boolean(homeCare) }), // For checkboxes, this is always a boolean
  });
};

export const updateLifeCare = async (
  patientId: string,
  lifeCare: boolean | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ lifeCare: Boolean(lifeCare) }), // For checkboxes, this is always a boolean
  });
};

export const updatePrimaryResponsible = async (
  patientId: string,
  primaryResponsible: number | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ primaryResponsible }),
  });
};

export const updateReferringUnit = async (
  patientId: string,
  referringUnit: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ referringUnit }),
  });
};

export const updatePlaceOfAssessment = async (
  patientId: string,
  placeOfAssessment: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ placeOfAssessment }),
  });
};

export const updatePaymentExempt = async (
  patientId: string,
  paymentExempt: boolean | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ paymentExempt }),
  });
};

export const updateLanguage = async (
  patientId: string,
  language: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ language }),
  });
};

export const updateReasonForAdmission = async (
  patientId: string,
  reasonForAdmission: string | null | undefined,
) => {
  await patientApi.patch(`/patients/${patientId}`, {
    json: generatePatches({ reasonForAdmission }),
  });
};

export const SMS_NOTIFICATION_MESSAGE_FROM_CHAT = msg`Hej! Du har ett nytt chattmeddelande från din vårdgivare. Öppna Medoma-appen och gå till chatten för att läsa.`;

export const notifyBySMS = async (patientId: string) => {
  await patientApi.post(`/patients/${patientId}/notify`, {
    json: { message: i18n._(SMS_NOTIFICATION_MESSAGE_FROM_CHAT) },
  });
};

export const getPatientNameWithStatus = (
  patient:
    | Pick<IDeletedPatient, "status">
    | Pick<IRegisteredPatient, "name" | "status">
    | Pick<IDischargedPatient, "name" | "status">
    | Pick<IDeclinedPatient, "name" | "status">
    | Pick<IAdmittedPatient, "name" | "status" | "pausedAt">,
  nameFormat: "family" | "given" | "full",
): string => {
  const formatName =
    nameFormat === "family"
      ? getPatientFamilyName
      : nameFormat === "given"
        ? getPatientGivenName
        : getPatientFullName;
  if (patient.status === patientStatusSchema.enum.admitted) {
    if (patient.pausedAt) {
      return `${formatName(patient.name)} (${t`pausad`})`;
    }
    return formatName(patient.name);
  }

  const localizedPatientStatus = i18n
    ._(patientStatusDictionary[patient.status].singular)
    .toLocaleLowerCase();

  if (patient.status === patientStatusSchema.enum.deleted) {
    return t`Okänd (${localizedPatientStatus})`;
  }

  return `${formatName(patient.name)} (${i18n
    ._(patientStatusDictionary[patient.status].singular)
    .toLocaleLowerCase()})`;
};

const getAllWards = async () => {
  return await patientApi.get("/wards").json(wardsSchema);
};

export const useWards = () => {
  return useQuery({
    queryKey: wardsKeys.all,
    queryFn: () => getAllWards(),
  });
};

const fetchAllSites = async () => {
  // Using "patient API" is a bit of a workaround. See https://medoma.slack.com/archives/C0A24R4FLJV/p1769163525178789 for context
  return await patientApi.get("/sites").json(z.array(siteSchema));
};

export const sitesQueryOptions = queryOptions({
  queryKey: ["sites"],
  queryFn: fetchAllSites,
  staleTime: 1000 * 60 * 10, // 10 minutes
});

const fetchAllAreas = async () => {
  return await patientApi.get("/areas").json(areasSchema);
};

export const useAreas = () => {
  return useQuery({
    queryKey: areasKeys.all,
    queryFn: () => fetchAllAreas(),
  });
};

const fetchAllTeams = async () => {
  return await patientApi.get(`/teams`).json(teamsSchema);
};

export const useTeams = () => {
  return useQuery({
    queryKey: teamsKeys.all,
    queryFn: () => fetchAllTeams(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

const fetchAllEconomicUnits = async () => {
  return await patientApi.get(`/economic-units`).json(economicUnitsSchema);
};

export const useEconomicUnits = () => {
  return useQuery({
    queryKey: economicUnitsKeys.all,
    queryFn: () => fetchAllEconomicUnits(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const patientsWithUncancelledHomeVisitsQueryOptions = ({
  fromDate,
  toDate,
}: {
  fromDate: Date;
  toDate: Date;
}) => {
  const from = machineFormat(fromDate, "yyyy-MM-dd");
  const to = machineFormat(toDate, "yyyy-MM-dd");
  return queryOptions({
    queryKey: patientKeys.listWithVariant({ from, to }),
    queryFn: () => fetchPatientsWithUncancelledHomeVisits(from, to),
    staleTime: 1000 * 60 * 10,
  });
};

async function fetchPatientsWithUncancelledHomeVisits(
  from?: string,
  to?: string,
) {
  const homeVisitOccurrences =
    await fetchUncancelledHomeVisitActivityOccurrences(from, to);

  const patientIdsWithVisits = [
    ...new Set(homeVisitOccurrences.map((occurrence) => occurrence.patient.id)),
  ];

  const [activePatients, dischargedPatientsWithVisits] = await Promise.all([
    fetchPatients({ statuses: activeStatuses }),
    patientIdsWithVisits.length > 0
      ? fetchPatients({
          statuses: [patientStatusSchema.enum.discharged],
          patientIds: patientIdsWithVisits,
        })
      : Promise.resolve([]),
  ]);

  const patients = [...activePatients, ...dischargedPatientsWithVisits];

  return patients.map((patient) => ({
    ...patient,
    homeVisitOccurrences: homeVisitOccurrences.filter(
      (occurrence) => occurrence.patient.id === patient.id,
    ),
  }));
}

/*****************************************************************************************************
 * CONDITIONAL PATIENT INFORMATION FIELDS
 *****************************************************************************************************/

export const conditionalPatientInformationFieldNames = [
  "DiagnosisCode",
  "AdditionalAdressInformation",
  "KeyToPatient",
  "CloseContact",
  "HomeCare",
  "Pet",
  "PersWristband",
  "MedicalTransportNumber",
  "LifeCare",
  "GeneralInformation",
  "ExternalCareEpisodeId",
  "Flags",
  "PrimaryResponsible",
  "ReferringUnit",
  "PlaceOfAssessment",
  "PlannedAdmissionDateTime",
  "PlannedDischargeDateTime",
  "PostDischargeProcessed",
  "PaymentExempt",
  "CareSummary",
  "Language",
  "ReasonForAdmission",
] as const;

export const conditionalPatientInformationFieldNamesDictionary = {
  DiagnosisCode: msg`Diagnoskod`,
  AdditionalAdressInformation: msg`Övrig adressinformation`,
  KeyToPatient: msg`Nyckel`,
  CloseContact: msg`Anhöriga`,
  HomeCare: msg`Hemtjänst`,
  Pet: msg`Husdjur`,
  PersWristband: msg`Sociala insatser`,
  MedicalTransportNumber: msg`Sjukresenummer`,
  LifeCare: msg`LifeCare`,
  GeneralInformation: msg`Allmän information`,
  ExternalCareEpisodeId: msg`Externt vårdperiodnummer`,
  Flags: msg`Flaggningar`,
  PrimaryResponsible: msg`Patientansvarig`,
  ReferringUnit: msg`Remitterande enhet`,
  PlaceOfAssessment: msg`Plats för bedömning`,
  PlannedAdmissionDateTime: msg`Planerad inskrivning`,
  PlannedDischargeDateTime: msg`Planerad utskrivning`,
  PostDischargeProcessed: msg`Efterhanterad`,
  PaymentExempt: msg`Betalningsbefriad`,
  CareSummary: msg`Vårdsammanfattning`,
  Language: msg`Språk`,
  ReasonForAdmission: msg`Inskrivningsorsak`,
};

export const conditionalPatientInformationFieldNamesSchema = z.enum(
  conditionalPatientInformationFieldNames,
);

const availablePatientInformationFieldSchema = z.object({
  fieldName: conditionalPatientInformationFieldNamesSchema,
  required: z.boolean(),
});

export const availablePatientInformationFieldsSchema = z.array(
  availablePatientInformationFieldSchema,
);

export type IAvailablePatientInformationFields = z.infer<
  typeof availablePatientInformationFieldsSchema
>;

const fetchPatientInformationFields = async () => {
  const parsedData = await patientApi
    .get("/patients/information-fields")
    .json(availablePatientInformationFieldsSchema);
  return parsedData;
};

export const informationFieldConfigValueSchema = z.enum([
  "hidden",
  "optional",
  "required",
]);
export type IInformationFieldConfigValue = z.infer<
  typeof informationFieldConfigValueSchema
>;

const _informationFieldsConfigSchema = z.record(
  conditionalPatientInformationFieldNamesSchema,
  informationFieldConfigValueSchema,
);
export type IInformationFieldsConfig = z.infer<
  typeof _informationFieldsConfigSchema
>;

const createInformationFieldsConfig = (
  availableFields: IAvailablePatientInformationFields,
) => {
  const config = {} as IInformationFieldsConfig;

  availableFields.forEach((item) => {
    config[item.fieldName] = item.required
      ? informationFieldConfigValueSchema.enum.required
      : informationFieldConfigValueSchema.enum.optional;
  });

  conditionalPatientInformationFieldNames.forEach((field) => {
    if (!config[field]) {
      config[field] = informationFieldConfigValueSchema.enum.hidden;
    }
  });

  return config;
};

// Fields that are only meaningful for a subset of patient statuses live here next to the
// label and config so the per-field rule has one home. A field without an entry is visible
// whenever its config value isn't `hidden`.
const conditionalPatientInformationFieldStatusGate: Partial<
  Record<
    (typeof conditionalPatientInformationFieldNames)[number],
    (status: IPatientStatus) => boolean
  >
> = {
  PlannedAdmissionDateTime: (status) =>
    status === patientStatusSchema.enum.prospect,
  PlannedDischargeDateTime: (status) =>
    status !== patientStatusSchema.enum.prospect,
  PlaceOfAssessment: (status) => status === patientStatusSchema.enum.prospect,
  PostDischargeProcessed: (status) =>
    status === patientStatusSchema.enum.discharged,
};

export const isPatientInformationFieldVisible = ({
  fieldName,
  configValue,
  patientStatus,
}: {
  fieldName: (typeof conditionalPatientInformationFieldNames)[number];
  configValue: IInformationFieldConfigValue;
  patientStatus: IPatientStatus;
}) => {
  if (configValue === informationFieldConfigValueSchema.enum.hidden) {
    return false;
  }
  const gate = conditionalPatientInformationFieldStatusGate[fieldName];
  return gate ? gate(patientStatus) : true;
};

const patientInformationFieldsKeys = {
  all: ["patientInformationFields"] as const,
};

export const usePatientInformationFields = () => {
  return useQuery({
    queryKey: [patientInformationFieldsKeys.all],
    queryFn: async () => {
      const availableFields = await fetchPatientInformationFields();
      const informationFields = createInformationFieldsConfig(availableFields);
      return informationFields;
    },
  });
};

export const fetchCareEpisodes = async ({
  queryParams,
}: {
  queryParams: {
    limit?: number;
    sort?: "desc" | "asc";
    "swedish-identity-number"?: string;
    "temporary-number"?: string;
    "swiss-emedicus-number"?: string;
    "swiss-patient-number"?: string;
    "latvian-personas-kods"?: string;
  };
}) => {
  const queryString = generateQueryString(queryParams);
  const parsedCareEpisodes = await patientApi
    .get(`/care-episodes${queryString}`)
    .json(z.array(careEpisodeSchema));
  return parsedCareEpisodes;
};

export const patientIdentifierQueryParamKey = {
  SwedishPersonalIdentityNumber: "swedish-identity-number",
  SwedishTemporaryIdentityNumber: "temporary-number",
  SwissEMedicusNumber: "swiss-emedicus-number",
  SwissPatientNumber: "swiss-patient-number",
  LatvianPersonasKods: "latvian-personas-kods",
} as const satisfies Record<IPersonalIdentityNumberType, string>;

export const normalizePatientIdentifier = ({
  identifier,
  identifierType,
}: {
  identifier: string;
  identifierType: IPersonalIdentityNumberType;
}) => {
  switch (identifierType) {
    case personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber:
      return identifier.replace("-", "");
    case personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber:
      return identifier;
    case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
      return identifier;
    case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
      return identifier;
    case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
      return identifier.replace("-", "");
    default:
      throw identifierType satisfies never;
  }
};

export const patientLatestCareEpisodeQueryOptions = ({
  identifier,
  identifierType,
}: {
  identifier: string;
  identifierType: IPersonalIdentityNumberType;
}) => {
  const limit = 1;
  const sort = "desc" as const;

  let queryParams: {
    limit: number;
    sort: "desc";
    "swedish-identity-number"?: string;
    "temporary-number"?: string;
    "swiss-emedicus-number"?: string;
    "swiss-patient-number"?: string;
    "latvian-personas-kods"?: string;
  };

  switch (identifierType) {
    case personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber:
      queryParams = {
        limit,
        sort,
        "swedish-identity-number": normalizePatientIdentifier({
          identifier,
          identifierType,
        }),
      };
      break;
    case personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber:
      queryParams = {
        limit,
        sort,
        "temporary-number": normalizePatientIdentifier({
          identifier,
          identifierType,
        }),
      };
      break;
    case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
      queryParams = {
        limit,
        sort,
        "swiss-emedicus-number": normalizePatientIdentifier({
          identifier,
          identifierType,
        }),
      };
      break;
    case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
      queryParams = {
        limit,
        sort,
        "swiss-patient-number": normalizePatientIdentifier({
          identifier,
          identifierType,
        }),
      };
      break;
    case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
      queryParams = {
        limit,
        sort,
        "latvian-personas-kods": normalizePatientIdentifier({
          identifier,
          identifierType,
        }),
      };
      break;
    default:
      throw identifierType satisfies never;
  }

  return queryOptions({
    queryKey: ["patientLatestCareEpisode", identifier, queryParams],
    queryFn: async () => {
      const [latestCareEpisode] = await fetchCareEpisodes({ queryParams });
      if (!latestCareEpisode) {
        throw new Error(`No care episode found for ${identifier}.`);
      }
      return latestCareEpisode;
    },
  });
};

const sparPersonSchema = z.object({
  givenName: z.string().nullish(),
  familyName: z.string().nullish(),
  address: z
    .object({
      addressLine1: z.string().nullish(),
      addressLine2: z.string().nullish(),
      postalCode: z.string().nullish(),
      city: z.string().nullish(),
    })
    .nullish(),
});

export type ISparPerson = z.infer<typeof sparPersonSchema>;

export const sparPersonQueryOptions = ({ pin }: { pin: string }) =>
  queryOptions({
    queryKey: ["sparPerson", pin],
    queryFn: async () => {
      // SPAR returns a list of matches, but we only ever use the first entry.
      const [sparPerson] = await patientApi
        .get(`/patients/spar`, { searchParams: { pin } })
        .json(z.array(sparPersonSchema));
      return sparPerson ?? null;
    },
  });

export const registerNewCareEpisode = async (careEpisode: INewCareEpisode) => {
  await patientApi.post(`/care-episodes`, {
    json: newCareEpisodeSchema.encode(careEpisode),
  });
};

export const registerNewProspectCareEpisode = async (
  careEpisode: INewProspectCareEpisode,
) => {
  await patientApi.post(`/care-episodes`, {
    json: newProspectCareEpisodeSchema.encode(careEpisode),
  });
};

// Admission takes `admissionDate` as a plain date and derives the timestamp server-side:
// admitting on today's date stamps the current time, an earlier date stamps end of day.
// The backend rejects a date in the future or before the patient was registered.
const admitPatientSchema = z.object({
  admissionDate: calendarDateSchema,
  address: careEpisodeAddressSchema,
  // The site-specific ids, as in a registration. The economic unit is not among them:
  // admission does not move a patient between economic units, and patient information is
  // where that is changed.
  area: z.number().positive().nullish(),
  team: z.number().positive().nullish(),
  ward: z.number().positive().nullish(),
  ...careEpisodeInformationSchema.omit({
    // Not asked for while admitting; set from patient information.
    language: true,
  }).shape,
  // What every other endpoint calls `phoneNumber`.
  phone: rawPhoneNumberSchema,
  // At most one close contact can be added while admitting. Patients who already have
  // relatives are shown them read-only; further changes go through patient information.
  // A name and a relation identify the contact; a phone number is a way to reach them that
  // the admitting nurse may not have. Not `relativeSchema`: the contact has no id yet, and
  // this endpoint spells its phone number differently too.
  relative: z
    .object({
      name: z.string(),
      phone: rawPhoneNumberSchema.nullish(),
      isLivingTogether: z.boolean(),
      relation: z.string(),
    })
    .nullish(),
});
export type IAdmitPatient = z.infer<typeof admitPatientSchema>;

export const admitPatient = async ({
  patientId,
  admission,
}: {
  patientId: string;
  admission: IAdmitPatient;
}) => {
  await patientApi.post(`/patients/${patientId}/admit`, {
    json: admitPatientSchema.encode(admission),
  });
};
